import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info, RefreshCw, X } from 'lucide-react';
import { formatInfraDateTime } from '../utils/infraAnalytics';
import sinalCtoClientesService from '../services/sinalCtoClientesService';

const panelClassName =
  'rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)]';

const STATUS_CONTRATO_LABELS = {
  A: 'Ativo',
  I: 'Inativo',
  N: 'Negativado',
  D: 'Desistente',
  P: 'Pendente',
};

const ONLINE_LABELS = {
  S: 'Online',
  N: 'Offline',
  SS: 'Sem status',
  I: 'Indefinido',
};

const signalFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const isAbortError = (error) => (
  error?.name === 'AbortError'
  || error?.name === 'CanceledError'
  || error?.code === 'ERR_CANCELED'
);

const isIxcUnavailable = (error) => error?.response?.data?.code === 'IXC_UNAVAILABLE';

const formatSignal = (value) => (value === null ? '—' : `${signalFormatter.format(value)} dBm`);

const SinalCtoClientesPanel = ({ cto, onClose = null }) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);

  const idCto = cto?.id_cto ?? null;
  const retry = useCallback(() => setReloadToken((current) => current + 1), []);

  useEffect(() => {
    if (idCto === null || idCto === undefined) {
      return undefined;
    }

    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const carregar = async () => {
      setLoading(true);
      setErrorMessage('');
      setUnavailable(false);

      try {
        const result = await sinalCtoClientesService.buscarClientesDaCto(idCto, controller.signal);

        if (requestId !== requestIdRef.current) {
          return;
        }

        setClientes(result);
      } catch (error) {
        if (isAbortError(error) || requestId !== requestIdRef.current) {
          return;
        }

        setClientes([]);
        if (isIxcUnavailable(error)) {
          setUnavailable(true);
          setErrorMessage(error?.response?.data?.message
            || 'A leitura de clientes por CTO ainda não está habilitada para esta empresa.');
        } else {
          setErrorMessage(error?.response?.data?.message
            || error?.message
            || 'Não foi possível carregar os clientes desta caixa.');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    carregar();

    return () => controller.abort();
  }, [idCto, reloadToken]);

  const naoVinculados = useMemo(
    () => clientes.filter((item) => !item.vinculado).length,
    [clientes],
  );

  if (idCto === null || idCto === undefined) {
    return null;
  }

  return (
    <div className={panelClassName} data-testid="sinal-cto-clientes-panel">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
            Clientes da caixa {cto?.caixa || `#${idCto}`}
          </h3>
          <p className="mt-1 text-xs text-[color:var(--text-muted)]">
            {`ID CTO ${idCto} · ${clientes.length.toLocaleString('pt-BR')} ONUs na caixa`}
            {naoVinculados > 0 ? ` · ${naoVinculados.toLocaleString('pt-BR')} sem cliente vinculado` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {loading ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Carregando
            </span>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar painel de clientes"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/45"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {errorMessage ? (
        <div
          className={`flex items-start gap-3 rounded-[16px] border px-3 py-2.5 text-xs leading-5 ${
            unavailable
              ? 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/55 text-[color:var(--text-muted)]'
              : 'border-amber-500/25 bg-amber-500/10 text-[color:var(--text-muted)]'
          }`}
        >
          {unavailable
            ? <Info className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--accent)]/85" />
            : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-[color:var(--text-primary)]">
              {unavailable ? 'Consulta indisponível' : 'Falha ao carregar clientes'}
            </p>
            <p>{errorMessage}</p>
          </div>
          <button
            type="button"
            onClick={retry}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/45"
          >
            <RefreshCw className="h-3 w-3" />
            Tentar novamente
          </button>
        </div>
      ) : null}

      {!errorMessage ? (
        <div
          data-testid="sinal-cto-clientes-scroll"
          className="overflow-x-auto rounded-xl border border-[color:var(--border)]/60"
        >
          <table className="min-w-[880px] w-full text-xs">
            <caption className="sr-only">Clientes e ONUs vinculados à caixa selecionada</caption>
            <thead className="bg-[color:var(--surface-strong)]">
              <tr className="border-b border-[color:var(--border)] text-left text-[color:var(--text-muted)]">
                <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Porta</th>
                <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Cliente</th>
                <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Contrato</th>
                <th scope="col" className="px-2 py-2 text-right font-semibold uppercase tracking-[0.06em]">Sinal RX</th>
                <th scope="col" className="px-2 py-2 text-right font-semibold uppercase tracking-[0.06em]">Sinal TX</th>
                <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Classificação</th>
                <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Conexão</th>
                <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Última leitura</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((item) => (
                <tr key={item.id_onu} className="border-b border-[color:var(--border)]/40 hover:bg-[color:var(--surface-muted)]/40">
                  <td className="px-2 py-2">{item.porta_ftth ?? '—'}</td>
                  <td className="px-2 py-2">
                    {item.vinculado ? (
                      <span className="text-[color:var(--text-primary)]">
                        {item.cliente || `Cliente #${item.id_cliente}`}
                      </span>
                    ) : (
                      <span className="text-[color:var(--text-muted)]">ONU sem cliente vinculado</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {item.id_contrato
                      ? `#${item.id_contrato}${item.status_contrato ? ` · ${STATUS_CONTRATO_LABELS[item.status_contrato] || item.status_contrato}` : ''}`
                      : '—'}
                  </td>
                  <td className="px-2 py-2 text-right">{formatSignal(item.sinal_rx)}</td>
                  <td className="px-2 py-2 text-right">{formatSignal(item.sinal_tx)}</td>
                  <td className="px-2 py-2">{item.classificacao_sinal || '—'}</td>
                  <td className="px-2 py-2">
                    {item.login_online ? (ONLINE_LABELS[item.login_online] || item.login_online) : '—'}
                    {item.causa_ultima_queda ? (
                      <span className="block text-[10px] text-[color:var(--text-muted)]">
                        {item.causa_ultima_queda}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">
                    {item.data_sinal ? formatInfraDateTime(item.data_sinal) : '—'}
                  </td>
                </tr>
              ))}
              {clientes.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className="px-2 py-8 text-center text-sm text-[color:var(--text-muted)]">
                    Nenhuma ONU registrada nesta caixa.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default SinalCtoClientesPanel;
